import * as THREE from "three";
import type { Vector3Like } from "@ict/shared";
import { CharacterModel } from "./CharacterModel";

const UP = new THREE.Vector3(0, 1, 0);
const WALK_SPEED = 3.2;
const RUN_SPEED = 6.0;
const JUMP_SPEED = 5.5;
const GRAVITY = -16;
const ARENA_RADIUS = 28.5;
const CAMERA_DISTANCE = 6.5;
const CAMERA_BASE_HEIGHT = 2.4;

export interface TouchInputState {
  moveX: number; // -1..1 strafe
  moveY: number; // -1..1 forward
  run: boolean;
  jumpPressed: boolean;
}

/** Local player: keyboard/mouse (and optional touch) input, movement, third-person camera, and the visible rig. */
export class PlayerController {
  readonly model = new CharacterModel("/models/player-local.glb");

  private position: THREE.Vector3;
  private velocityY = 0;
  private grounded = true;
  private rotationY = 0;

  private yaw = Math.PI;
  private pitch = 0.35;

  private keys = new Set<string>();
  private inputEnabled = true;
  private touchInput: TouchInputState = { moveX: 0, moveY: 0, run: false, jumpPressed: false };
  private lastMoved = false;

  private touchLookId: number | null = null;
  private touchLookX = 0;
  private touchLookY = 0;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly domElement: HTMLElement,
    spawn: Vector3Like
  ) {
    this.position = new THREE.Vector3(spawn.x, spawn.y, spawn.z);
    this.model.root.position.copy(this.position);
    this.scene.add(this.model.root);

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.domElement.addEventListener("click", this.onClickRequestLock);
    document.addEventListener("mousemove", this.onMouseMove);

    // Touch camera-look: any single-finger drag on the canvas itself (the
    // movement joystick and action buttons are separate DOM elements that
    // capture their own touches, so this never conflicts with them).
    this.domElement.addEventListener("touchstart", this.onTouchStart, { passive: true });
    this.domElement.addEventListener("touchmove", this.onTouchMove, { passive: false });
    this.domElement.addEventListener("touchend", this.onTouchEnd, { passive: true });
    this.domElement.addEventListener("touchcancel", this.onTouchEnd, { passive: true });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onClickRequestLock = (): void => {
    if (!this.inputEnabled) return;
    if (document.pointerLockElement !== this.domElement) {
      // Mobile browsers commonly reject pointer lock (or lack it entirely) —
      // camera control there comes from touch-drag instead, so a rejection
      // here is expected and harmless, not an error to surface.
      const result = this.domElement.requestPointerLock?.();
      if (result && typeof (result as Promise<void>).catch === "function") {
        (result as Promise<void>).catch(() => undefined);
      }
    }
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (document.pointerLockElement !== this.domElement || !this.inputEnabled) return;
    this.yaw -= e.movementX * 0.0022;
    this.pitch = clamp(this.pitch - e.movementY * 0.0018, -0.15, 0.95);
  };

  private onTouchStart = (e: TouchEvent): void => {
    if (!this.inputEnabled || this.touchLookId !== null) return;
    const t = e.changedTouches[0];
    this.touchLookId = t.identifier;
    this.touchLookX = t.clientX;
    this.touchLookY = t.clientY;
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (this.touchLookId === null || !this.inputEnabled) return;
    const t = [...e.changedTouches].find((touch) => touch.identifier === this.touchLookId);
    if (!t) return;
    e.preventDefault();
    const dx = t.clientX - this.touchLookX;
    const dy = t.clientY - this.touchLookY;
    this.touchLookX = t.clientX;
    this.touchLookY = t.clientY;
    this.yaw -= dx * 0.0045;
    this.pitch = clamp(this.pitch - dy * 0.0035, -0.15, 0.95);
  };

  private onTouchEnd = (e: TouchEvent): void => {
    if ([...e.changedTouches].some((t) => t.identifier === this.touchLookId)) {
      this.touchLookId = null;
    }
  };

  setInputEnabled(enabled: boolean): void {
    this.inputEnabled = enabled;
    if (!enabled) this.keys.clear();
  }

  setTouchInput(input: Partial<TouchInputState>): void {
    Object.assign(this.touchInput, input);
  }

  private isPointerLocked(): boolean {
    return document.pointerLockElement === this.domElement;
  }

  triggerAttack(): void {
    this.model.setState("attack");
  }

  getPosition(): Vector3Like {
    return { x: this.position.x, y: this.position.y, z: this.position.z };
  }

  getRotationY(): number {
    return this.rotationY;
  }

  didMoveThisFrame(): boolean {
    return this.lastMoved;
  }

  update(dt: number): void {
    const forward = new THREE.Vector3(0, 0, -1).applyAxisAngle(UP, this.yaw);
    const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(UP, this.yaw);

    let inputForward = 0;
    let inputStrafe = 0;
    let running = false;
    let jumpPressed = false;

    if (this.inputEnabled) {
      if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) inputForward += 1;
      if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) inputForward -= 1;
      if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) inputStrafe += 1;
      if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) inputStrafe -= 1;
      running = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") || this.touchInput.run;
      jumpPressed = this.keys.has("Space") || this.touchInput.jumpPressed;

      inputForward += this.touchInput.moveY;
      inputStrafe += this.touchInput.moveX;
    }

    const moveDir = new THREE.Vector3()
      .addScaledVector(forward, clamp(inputForward, -1, 1))
      .addScaledVector(right, clamp(inputStrafe, -1, 1));
    const moving = moveDir.lengthSq() > 0.0001;
    if (moving) moveDir.normalize();

    const speed = running ? RUN_SPEED : WALK_SPEED;
    this.position.addScaledVector(moveDir, speed * dt);

    // Arena bounds
    const r = Math.hypot(this.position.x, this.position.z);
    if (r > ARENA_RADIUS) {
      const scale = ARENA_RADIUS / r;
      this.position.x *= scale;
      this.position.z *= scale;
    }

    // Gravity / jump
    if (jumpPressed && this.grounded) {
      this.velocityY = JUMP_SPEED;
      this.grounded = false;
      this.model.setState("jump");
    }
    this.velocityY += GRAVITY * dt;
    this.position.y += this.velocityY * dt;
    if (this.position.y <= 0) {
      this.position.y = 0;
      this.velocityY = 0;
      this.grounded = true;
    }

    if (moving) {
      this.rotationY = Math.atan2(moveDir.x, moveDir.z);
    }
    this.model.root.position.copy(this.position);
    this.model.root.rotation.y = this.rotationY;

    if (this.grounded) {
      this.model.setState(moving ? (running ? "run" : "walk") : "idle");
    }
    this.model.update(dt);

    this.updateCamera();
    this.lastMoved = moving || !this.grounded;
    this.touchInput.jumpPressed = false;
  }

  private updateCamera(): void {
    const horizDist = CAMERA_DISTANCE * Math.cos(this.pitch);
    const offset = new THREE.Vector3(
      Math.sin(this.yaw) * horizDist,
      CAMERA_BASE_HEIGHT + CAMERA_DISTANCE * Math.sin(this.pitch),
      Math.cos(this.yaw) * horizDist
    );
    const target = this.position.clone().add(new THREE.Vector3(0, 1.4, 0));
    this.camera.position.copy(target).add(offset);
    this.camera.lookAt(target);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.domElement.removeEventListener("click", this.onClickRequestLock);
    document.removeEventListener("mousemove", this.onMouseMove);
    this.domElement.removeEventListener("touchstart", this.onTouchStart);
    this.domElement.removeEventListener("touchmove", this.onTouchMove);
    this.domElement.removeEventListener("touchend", this.onTouchEnd);
    this.domElement.removeEventListener("touchcancel", this.onTouchEnd);
    this.model.dispose();
    this.scene.remove(this.model.root);
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
